<template>
  <div class="mb-3">
    <TemplateListHeader
      :defaultSort="sortKey"
      :defaultOrder="sortOrder"
      :currentFilter="filter"
      :editorUrl="getEditorUrl()"
      @sortChanged="onSortChanged"
      @filterChanged="onFilterChanged"
      @search="onSearch"
    />

    <div v-if="!isLoading && templates.length === 0" class="text-center text-muted py-4 bg-light rounded shadow-sm">
      <i class="bi bi-search" style="font-size: 1.5rem;"></i>
      <div>No templates found.</div>
    </div>

    <TemplateList
      v-else
      :templates="templates"
      :isLoading="isLoading"
      :get-editor-url="getEditorUrl"
      @edit="handleEdit"
      @copy="handleCopy"
      @delete="handleDelete"
      @view="handleView"
    />

    <PaginationComponent
      :pagination="pagination"
      @change="onPageChanged"
    />

    <BootstrapModal
      ref="viewTemplateModal"
      modalId="viewTemplateModal"
      title="Template"
      closeText="Close"
      hideConfirmButton
    >
      <template #body>
        <div
          class="d-flex justify-content-center align-items-center"
          style="max-height: 70vh"
        >
          <img
            :src="adminUrl + (previewTemplate?.img_screen ?? '')"
            class="img-fluid"
            alt="Template"
          />
        </div>
      </template>
    </BootstrapModal>

    <DeleteTemplate
      ref="deleteTemplateModal"
      :template="selectedTemplate"
      @deleted="onTemplateDeleted"
    />

    <CopyTemplate
      ref="copyTemplateModal"
      :template="selectedTemplate"
      @copied="onTemplateCopied"
    />
  </div>
</template>

<script>
import BootstrapModal from "@/components/Common/BootstrapModal.vue";
import TemplateService from "@/services/templateService.js";
import TemplateListHeader from "@/components/Template/TemplateListHeader.vue";
import TemplateList from "@/components/Template/TemplateList.vue";
import PaginationComponent from "@/components/Common/Pagination.vue";
import DeleteTemplate from "@/components/Template/DeleteTemplate.vue";
import CopyTemplate from "@/components/Template/CopyTemplate.vue";

export default {
  emits: ["updateTitle"],
  components: {
    BootstrapModal,
    TemplateList,
    TemplateListHeader,
    PaginationComponent,
    DeleteTemplate,
    CopyTemplate,
  },
  data() {
    return {
      adminUrl: process.env.VUE_APP_TEMPLATE_THUMBNAIL_URL,
      isLoading: true,
      templates: [],
      sortKey: "id",
      sortOrder: "desc",
      filter: "all",
      lastQuery: {},
      page: 1,
      searchQuery: "",
      pendingSearch: "",
      previewTemplate: null,
      selectedTemplate: null,
      pagination: {
        current_page: 1,
        last_page: 1,
        per_page: 15,
        total: 0,
      },
    };
  },
  mounted() {
    this.fetchData();
  },
  methods: {
    async fetchData() {
      this.isLoading = true;
      try {
        const params = {
          sort_key: this.sortKey,
          sort_order: this.sortOrder,
          page: this.page,
          search: this.searchQuery,
        };
        if (this.filter === "active") {
          params.only_active = 1;
        }

        const currentQuery = JSON.stringify(params);
        if (currentQuery === this.lastQueryString) {
          this.isLoading = false;
          return;
        }

        this.lastQueryString = currentQuery;
        const data = await TemplateService.getTemplates(params);
        this.templates = data.data;
        this.pagination = {
          current_page: data.current_page,
          last_page: data.last_page,
          per_page: data.per_page,
          total: data.total,
        };
      } catch (error) {
        console.error("Error fetching templates", error);
      } finally {
        this.isLoading = false;
      }
    },

    onFilterChanged(filter) {
      if (filter !== this.filter) {
        this.filter = filter;
        this.page = 1;
        this.fetchData();
      }
    },

    onSortChanged({ sortKey, sortOrder, filter }) {
      this.sortKey = sortKey;
      this.sortOrder = sortOrder;
      this.filter = filter;
      this.page = 1;
      this.fetchData();
    },

    onPageChanged(newPage) {
      this.page = newPage;
      this.fetchData();
    },

    onSearch(event) {
      if (typeof event === "string") {
        this.searchQuery = event;
        this.page = 1;
        this.fetchData();
      } else if (event instanceof KeyboardEvent && event.key === "Enter") {
        this.searchQuery = event.target.value;
        this.page = 1;
        this.fetchData();
      }
    },

    handleEdit(template) {
      console.log("Edit template clicked", template);
    },

    handleDelete(template) {
      this.selectedTemplate = template;
      this.$refs.deleteTemplateModal.show();
    },

    handleCopy(template) {
      this.selectedTemplate = template;
      this.$refs.copyTemplateModal.show();
    },

    onTemplateDeleted() {
      this.lastQueryString = null;
      this.page = 1;
      this.fetchData();
    },

    onTemplateCopied() {
      this.lastQueryString = null;
      this.page = 1;
      this.fetchData();
    },
    
    handleView(template) {
      this.previewTemplate = template;
      this.$refs.viewTemplateModal.show();
    },

    getEditorUrl(templateId = '') {
      let editorBaseUrl = process.env.VUE_APP_TEMPLATE_EDITOR_URL;
      let apiGetTemplateUrl = process.env.VUE_APP_TEMPLATE_EDITOR_GET_URL;
      let apiSaveTemplateUrl = process.env.VUE_APP_TEMPLATE_EDITOR_SAVE_URL;

      return editorBaseUrl+'?tpl_id='+templateId+'&get_url='+apiGetTemplateUrl+'&save_url='+apiSaveTemplateUrl;
    },
  },
};
</script>